import React, {useEffect, useMemo, useState} from 'react';
import {
    Alert,
    AppHeader,
    DataList,
    Format,
    ListItemElement,
    RoundButton, Searchbox,
    Spinner,
    Tooltip
} from "@tag/tag-components-react-v4";
import {
    ArrowRewindLined,
    ClockFilled,
    DeleteFilled,
    DeleteLined,
    DesktopFilled,
    SearchFilled,
    UserMaleLined
} from "@tag/tag-icons";
import OrganisationService from "../services/OrganisationService";
import {ACPPTermination} from "../types/ACPPTermination";
import styles from "../OrganisationList/OrganisationList.module.scss";
import AppRolesService from "../../General/services/AppRolesService";
import {useIsAuthenticated, useMsal} from "@azure/msal-react";
import HelperMethods from "../../General/services/HelperMethods";
import UnlockConfirmationModal from "./UnlockConfirmationModal";
import {OrganisationDTO} from "../types/OrganisationDTO";

function Terminations() {

    const [allTerminations, setAllTerminations] = useState<ACPPTermination[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [selectedTermination, setSelectedTermination] = useState<ACPPTermination|undefined>();
    const [alert, setAlert] = useState<{type: any, message: string}>();
    const [searchTerm, setSearchTerm] = useState<string>('');

    const { accounts } = useMsal();
    const isAuthenticated = useIsAuthenticated();
    let canSoftDeleteEnv = AppRolesService.canUserSoftDeleteAcppEnvironments(accounts, isAuthenticated);
    
    useEffect(() => {
        loadOrganisationData()
    }, []);
    
    const loadOrganisationData = () => {
        setLoading(true);
        OrganisationService.getTerminations().then(n => {
            const updatedList = n.map((item: ACPPTermination) => {
                return {
                    ...item,
                    initials: HelperMethods.getInitials(item.organisationName),
                    primaryFieldValue: `${item.organisationName} (${item.environmentName})`
                };
            });
            setAllTerminations(updatedList);
            setLoading(false);
        })
    }
    
    const unlockCustomer = () => {
        setLoading(true);
        
        if (!canSoftDeleteEnv || !selectedTermination) {
            throw new Error()
        }
        
        OrganisationService.revertSoftDelete(selectedTermination.environmentName, selectedTermination?.rowKey)
            .then((response) => {
                setAlert({type: 'success', message: 'Organisation unmarked from deletion.'});
            })
            .catch((response) => {
                setAlert({type: 'error', message: 'An error has occured, please contact HSC Care DevOps'});
            })
            .finally(() => {
                setLoading(false)
                loadOrganisationData();
            })
    }

    const getTerminationsListData = (): ACPPTermination[] => {
        if (!searchTerm) {
            return allTerminations;
        }

        return  HelperMethods.advancedFuseSearch(searchTerm, allTerminations, ['organisationName', 'organisationID', 'environmentName']);
    }

    const terminationsData = useMemo(
        () => getTerminationsListData(),
        [searchTerm, allTerminations]
    );

    let content;
    if (!allTerminations && loading) {
        content = <Spinner full/>
    }
    else if (!allTerminations.length) {
        content = <>
            <h3>There are no terminations</h3>
        </>
    }
    else {
        content = <>
            <h1>Terminations</h1>
            <h4>Customers will be terminated after one month from the date they were locked</h4>
            <AppHeader
                heading="Organisation List"
                icon={<SearchFilled/>}
                accent={'purple'}
            >
                <div
                    style={{
                        alignItems: 'center',
                        display: 'flex',
                        justifyContent: 'space-between',
                    }}
                >
                    <Searchbox
                        onSearch={(e) => setSearchTerm(e)}
                        clearButton
                        style={{width: '100%'}}
                    />
                </div>
            </AppHeader>
            <DataList
                avatarProps={{srcField: 'image', placeholderField: 'initials'}}
                primaryField="primaryFieldValue"
                renderSubItemElements={(dataItem) => (
                    <>
                        <ListItemElement icon={<UserMaleLined accent="purple" />} label={'Initiated by:'}>{dataItem.requesterName}</ListItemElement>
                        <ListItemElement icon={<DesktopFilled accent="purple" />} label={'Reason: '}>{dataItem.reason}</ListItemElement>
                        {
                            dataItem.timestampLockedUtc ?
                            <ListItemElement icon={<ClockFilled accent="purple"/>} label="Locked at:">
                                <Format value={dataItem.timestampLockedUtc} dataType="datetime"/>
                            </ListItemElement> : undefined
                        }
                        {
                            dataItem.timestampDeletedUtc ?
                                <ListItemElement icon={<DeleteFilled accent="purple"/>} label="Deleted on:">
                                    <Format value={dataItem.timestampDeletedUtc} dataType="datetime"/>
                                </ListItemElement> : undefined
                        }
                    </>
                )}
                renderRightElements={(dataItem) => (
                    canSoftDeleteEnv ?
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Tooltip
                            background={'dark'}
                            placement={'left'}
                            tooltipFor={
                                <RoundButton
                                    disabled={!!dataItem.timestampDeletedUtc}
                                    onClick={() => setSelectedTermination(dataItem)}
                                    icon={<ArrowRewindLined />}
                                />
                            }
                        >
                            {
                                !dataItem.timestampDeletedUtc 
                                    ? "Restore environment" 
                                    : "Environment already deleted"
                            }
                            
                        </Tooltip>
                    </div> : <></>
                )}
                data={terminationsData}
            />
        </>
    }

    return (
        <>
            {content}

            {selectedTermination ?
                <UnlockConfirmationModal
                    selectedTermination={selectedTermination}
                    handleClose={() => {
                        setSelectedTermination(undefined)
                    }}
                    handleUnlock={unlockCustomer}
                    visible={!!selectedTermination}
                />:undefined
            }

            {alert ?
                <Alert
                    className={styles.OrganisationListAlert}
                    closeTransition="fade"
                    type={alert.type}
                    showDuration={1200}
                    onClosed={() => setAlert(undefined)}
                >
                    {alert.message}
                </Alert> : undefined
            }

            {loading ? <Spinner full /> : undefined}
        </>
    )

}

export default Terminations;
